// The full-screen Settings page (route /more/settings), opened from the
// gear on the Profile tab. Second restructure (owner, 2026-08-10): only
// account plumbing and rare/destructive actions live here — everything
// touched often (goals, reminders, units, preferences, admin) moved back
// to the Profile tab for quick access.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Link2, FileText, Trash2, LogOut, Download } from "lucide-react";
import { api } from "../lib/api";
import { useT, useLocale, apiLang } from "../lib/i18n";
import { logOutPurchases } from "../lib/purchases";
import { invalidateEntitlement } from "../lib/useEntitlement";
import { useConfirm } from "../lib/confirm";
import { useToast } from "../lib/toast";
import { useMe } from "../lib/useMe";
import { todayLocal } from "../lib/date";
import { SectionHeader } from "../components/ui/SectionHeader";
import {
  SettingsGroup, SettingsButton, SettingsAnchor, IconChip,
} from "../components/ui/SettingsList";
import { ChangePasswordSheet } from "../components/ChangePasswordSheet";
import { isNativeApp } from "../lib/native";

type Provider = "apple" | "google";
const PROVIDER_LABEL: Record<Provider, string> = { apple: "Apple", google: "Google" };

const APP_VERSION = "1.4";

const TINT = {
  security: "#64748b",
  about: "#64748b",
};

export function Settings({ onLogout }: { onLogout: () => void }) {
  const t = useT();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const { me } = useMe();

  const [showChangePassword, setShowChangePassword] = useState(false);
  const [configuredProviders, setConfiguredProviders] = useState<Provider[]>([]);
  const [verifySent, setVerifySent] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.authProviders().then((r) => setConfiguredProviders(r.providers)).catch(() => setConfiguredProviders([]));
  }, []);

  async function exportData() {
    try {
      const blob = await api.exportData("json");
      const filename = `caloly-export-${todayLocal()}.json`;
      if (isNativeApp()) {
        const { Filesystem, Directory } = await import("@capacitor/filesystem");
        const { Share } = await import("@capacitor/share");
        const text = await blob.text();
        const written = await Filesystem.writeFile({
          path: filename, data: text, directory: Directory.Cache,
          encoding: "utf8" as never,
        });
        await Share.share({ title: filename, url: written.uri });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
      }
      toast.success(t("export.done"));
    } catch {
      toast.error(t("common.failed"));
    }
  }

  async function logout() {
    if (!(await confirm({ message: t("more.confirm_sign_out"), confirmLabel: t("more.sign_out") }))) return;
    await api.logout();
    await logOutPurchases();
    invalidateEntitlement();
    onLogout();
    navigate("/login", { replace: true });
  }

  async function deleteAccount() {
    // Two distinct confirmations (owner: no accidental deletions): first the
    // consequences, then an explicit final ask.
    const first = await confirm({
      message: t("settings.deleteWarn1"),
      confirmLabel: t("common.continue"),
      danger: true,
    });
    if (!first) return;
    const ok = await confirm({
      message: t("settings.deleteWarn2"),
      confirmLabel: t("settings.deleteAccount"),
      danger: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await api.deleteAccount();
      window.location.replace("/"); // signed out — back to the landing page
    } catch {
      toast.error(t("common.failed"));
      setDeleting(false);
    }
  }

  async function resendVerify() {
    setVerifySent(true);
    try { await api.resendVerify(apiLang(locale)); } catch { /* best-effort, mirrors VerifyBanner */ }
  }

  return (
    <div className="space-y-5 pt-4 animate-rise">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate("/more")} aria-label={t("common.back")} className="text-fg-muted text-2xl leading-none -ml-1 press min-w-[44px] min-h-[44px] text-left">‹</button>
        <h1 className="text-title font-bold tracking-tight">{t("settings.title")}</h1>
      </div>

      {/* Account */}
      {me && (
        <section className="space-y-2">
          <SectionHeader>{t("auth.accountSection")}</SectionHeader>
          <SettingsGroup>
            {me.email && (
              <div className="flex items-center justify-between gap-3 px-4 py-3.5">
                <span className="flex-1 min-w-0 truncate text-sm">{me.email}</span>
                {me.email_verified ? (
                  <span className="text-sm px-2 py-0.5 bg-success/20 text-success rounded-full flex-shrink-0">
                    {t("auth.verifiedBadge")}
                  </span>
                ) : (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-sm px-2 py-0.5 bg-surface-strong rounded-full text-fg-muted">
                      {t("auth.unverifiedBadge")}
                    </span>
                    <button onClick={resendVerify} disabled={verifySent} className="text-sm underline text-fg-muted disabled:opacity-50">
                      {verifySent ? t("auth.verifySent") : t("auth.verifyResend")}
                    </button>
                  </div>
                )}
              </div>
            )}
            {me.email ? (
              <SettingsButton
                onClick={() => setShowChangePassword(true)}
                icon={Lock}
                tint={TINT.security}
                label={t("auth.changePassword")}
              />
            ) : (
              // Social-only account: no email/password yet, but a provider is linked.
              <SettingsButton
                onClick={() => setShowChangePassword(true)}
                icon={Lock}
                tint={TINT.security}
                label={t("auth.setPassword")}
              />
            )}
            {/* Link-account anchors navigate the page through the OAuth web
                flow — impossible from the bundled app (capacitor:// origin).
                Linked-state DISPLAY stays; the link ACTION is web-only until
                the native link-ticket flow ships with Universal Links. */}
            {(me.email || me.providers.length > 0) &&
              configuredProviders.map((p) => {
                const isLinked = me.providers.includes(p);
                if (!isLinked && isNativeApp()) return null;
                return isLinked ? (
                  <div key={p} className="flex items-center gap-3 px-4 py-3.5">
                    <IconChip icon={Link2} tint={TINT.security} />
                    <span className="flex-1 text-sm">{PROVIDER_LABEL[p]}</span>
                    <span className="text-sm px-2 py-0.5 bg-surface-strong rounded-full text-fg-muted">{t("auth.linked")}</span>
                  </div>
                ) : (
                  <SettingsAnchor
                    key={p}
                    href={`/api/auth/oauth/${p}/start?link=1`}
                    icon={Link2}
                    tint={TINT.security}
                    label={t(p === "apple" ? "auth.linkApple" : "auth.linkGoogle")}
                  />
                );
              })}
          </SettingsGroup>
        </section>
      )}

      {/* About */}
      <section className="space-y-2">
        <SectionHeader>{t("settings.about")}</SectionHeader>
        <SettingsGroup>
          <SettingsButton onClick={exportData} icon={Download} tint={TINT.about} label={t("export.row")} />
          <SettingsAnchor href="/privacy" icon={FileText} tint={TINT.about} label={t("settings.privacy")} />
          <SettingsAnchor href="/impressum" icon={FileText} tint={TINT.about} label={t("settings.impressum")} />
        </SettingsGroup>
      </section>

      {/* Sign out — its own group, iOS style */}
      {me && (
        <SettingsGroup>
          <SettingsButton onClick={logout} icon={LogOut} tint={TINT.security} label={t("more.sign_out")} />
        </SettingsGroup>
      )}

      {/* Danger zone */}
      <section className="space-y-2">
        <SectionHeader>{t("settings.dangerZone")}</SectionHeader>
        <SettingsGroup>
          <SettingsButton
            onClick={deleteAccount}
            disabled={deleting}
            icon={Trash2}
            tint={TINT.security}
            danger
            label={t("settings.deleteAccount")}
          />
        </SettingsGroup>
      </section>

      <p className="text-center text-sm text-fg-faint leading-relaxed px-4">{t("disclaimer.full")}</p>
      <p className="text-center text-sm text-fg-faint pb-1">caloly · {t("settings.version")} {APP_VERSION}</p>

      <ChangePasswordSheet open={showChangePassword} onClose={() => setShowChangePassword(false)} />
    </div>
  );
}
